import React from 'react';
import { useTranslation } from 'react-i18next';

import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Menu from '@mui/material/Menu';
import { alpha } from '@mui/material';
import Table from '@mui/material/Table';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import MenuItem from '@mui/material/MenuItem';
import TableRow from '@mui/material/TableRow';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import InputAdornment from '@mui/material/InputAdornment';
import TableContainer from '@mui/material/TableContainer';
import TableSortLabel from '@mui/material/TableSortLabel';
import TablePagination from '@mui/material/TablePagination';

import Iconify from '../../theme/iconify/iconify';

import NewGroup from './modal/NewGroup';
import { fDate } from '../util/format-time';
import { mislecAPI } from '../../util/axios';
import GroupMembers from './modal/GroupMembers';
import ConfirmationModal from '../util/ConfirmationModal';

function UserGroup({ setOpen, setMessage, setSeverity }) {
    const [users, setUsers] = React.useState(null);
    const [groups, setGroups] = React.useState(null);
    const [filteredGroups, setFilteredGroups] = React.useState(null);
    const [search, setSearch] = React.useState('');
    const [order, setOrder] = React.useState('asc');
    const [orderBy, setOrderBy] = React.useState(null);
    const [page, setPage] = React.useState(0);
    const [rowsPerPage, setRowsPerPage] = React.useState(parseInt(localStorage.getItem('mISLec_group_rows_per_page')) || 5);
    const [startIndex, setStartIndex] = React.useState(null);
    const [endIndex, setEndIndex] = React.useState(null);
    const [menuAnchor, setMenuAnchor] = React.useState(null);
    const [groupId, setGroupId] = React.useState(null);
    const [deleteGroupModal, setDeleteGroupModal] = React.useState(false);
    const [newGroupModal, setNewGroupModal] = React.useState(false);
    const [groupMembersModal, setGroupMembersModal] = React.useState(false);
    const menu = Boolean(menuAnchor);

    const { i18n, t } = useTranslation();

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value));
        localStorage.setItem('mISLec_group_rows_per_page', parseInt(event.target.value));
        if (parseInt(event.target.value) === -1) {
            setStartIndex(0);
            setEndIndex(filteredGroups.length);
        } else {
            setStartIndex(page * parseInt(event.target.value));
            setEndIndex(page * parseInt(event.target.value) + parseInt(event.target.value));
        }
        setPage(0);
    };

    const handleRequestSort = (event, property) => {
        if (orderBy === property) {
            if (order === 'asc') {
                setOrder('desc');
            } else {
                setOrderBy(null);
                setOrder('asc');
            }
        } else {
            setOrderBy(property);
            setOrder('asc');
        }
    }

    React.useEffect(() => {
        if (!groups) return;

        let sorted = [...groups];
        if (orderBy) {
            sorted.sort((a, b) => {
                if (orderBy === 'created') {
                    const valueA = a[orderBy] ? new Date(a[orderBy]) : order === 'asc' ? Infinity : -Infinity;
                    const valueB = b[orderBy] ? new Date(b[orderBy]) : order === 'asc' ? Infinity : -Infinity;
                    return (valueA - valueB) * (order === 'desc' ? -1 : 1);
                } else {
                    return (a[orderBy] < b[orderBy] ? -1 : 1) * (order === 'desc' ? -1 : 1);
                }
            });
        }

        setFilteredGroups(sorted);
    }, [groups, orderBy, order]);

    React.useEffect(() => {
        setFilteredGroups(groups?.filter(g => g.name.toLowerCase().includes(search.toLowerCase())));
    }, [search, groups]);

    const fetchGroups = () => {
        mislecAPI.get(`/api/groups`)
            .then(res => {
                setGroups(res.data.groups);
                setFilteredGroups(res.data.groups);
            })
            .catch(error => {
                //  Notify user of error
                setMessage(error.response ? t(error.response.data.detail) : t('api.general.500'));
                setSeverity('error');
                setOpen(true);
            });
    }

    const createGroup = async (data) => {
        let STATUS = false;
        if (data.name === '') {
            setOpen(true);
            setMessage(t('users.error.group.name'));
            setSeverity('warning');
            return;
        }

        //  Send data to the server
        await mislecAPI.post(`/api/groups`, data)
            .then(res => {
                STATUS = true;

                //  Notify user of success
                setMessage(t(res.data.detail));
                setSeverity('success');
                setOpen(true);

                //  Refresh groups
                fetchGroups();

            })
            .catch(error => {
                //  Notify user of error
                setMessage(error.response ? t(error.response.data.detail) : t('api.general.500'));
                setSeverity('error');
                setOpen(true);
            });

        return STATUS;
    }

    const deleteGroup = () => {
        //  Send data to the server
        mislecAPI.delete(`/api/groups/${groupId}`)
            .then(res => {
                //  Notify user of success
                setMessage(t(res.data.detail));
                setSeverity('success');
                setOpen(true);

                //  Refresh groups
                fetchGroups();
            })
            .catch(error => {
                //  Notify user of error
                setMessage(error.response ? t(error.response.data.detail) : t('api.general.500'));
                setSeverity('error');
                setOpen(true);
            });

        //  Cleanup
        setMenuAnchor(null);
        setDeleteGroupModal(false);
        setGroupId(null);
    }

    React.useEffect(() => {
        if (filteredGroups) return;
        fetchGroups();
    }, [filteredGroups]);

    React.useEffect(() => {
        if (!filteredGroups) return;

        if (rowsPerPage === -1) {
            setStartIndex(0);
            setEndIndex(filteredGroups.length);
        } else {
            setStartIndex(page * rowsPerPage);
            setEndIndex(page * rowsPerPage + rowsPerPage);
        }
    }, [filteredGroups, page, rowsPerPage]);

    React.useEffect(() => {
        setPage(0);
    }, [search]);

    React.useEffect(() => {
        if (users) return;

        mislecAPI.get('/api/groups/users').then(res => {
            setUsers(res.data.users);
        }).catch(error => {
            //  Notify user of error
            setMessage(error.response ? t(error.response.data.detail) : t('api.general.500'));
            setSeverity('error');
            setOpen(true);
        });
    }, [users]);

    return (
        <>
            <Grid container spacing={1}>
                <Grid item xs={12}>
                    <Grid container sx={{ display: 'flex', flexDirection: { xs: 'column-reverse', md: 'row' } }}>
                        <Grid item xs={12} md={4}>
                            <TextField
                                id='mISLec_user_group_search'
                                placeholder={t('users.inputs.group.label')}
                                variant='outlined'
                                size='small'
                                value={search}
                                onChange={e => setSearch(e.target.value)}
                                fullWidth
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position='end'>
                                            <IconButton disabled edge='start'>
                                                <Iconify icon='ion:search' />
                                            </IconButton>
                                        </InputAdornment>
                                    )
                                }}
                                sx={{
                                    '& .MuiInputBase-input': { fontSize: '0.875rem' },
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} md={8} sx={{ display: 'flex', justifyContent: 'flex-end', mb: { xs: 2, md: 0 } }}>
                            <Button
                                variant=''
                                startIcon={<Iconify icon='tdesign:usergroup-add-filled' />}
                                onClick={() => setNewGroupModal(true)}
                                sx={{
                                    borderRadius: '8px !important',
                                    backgroundColor: 'rgba(33, 43, 54, 0.08)',
                                    '&:hover': {
                                        backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.08),
                                        color: 'primary.main'
                                    }
                                }}
                            >
                                {t('users.buttons.newGroup')}
                            </Button>
                        </Grid>
                    </Grid>
                </Grid>
                <Grid item xs={12}>
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ backgroundColor: '#FFFFFF' }}>
                                        <TableSortLabel
                                            active={orderBy === 'name'}
                                            direction={orderBy === 'name' ? order : 'asc'}
                                            onClick={(event) => handleRequestSort(event, 'name')}
                                        >
                                            <Typography sx={{ fontSize: '0.875rem', fontWeight: '700 !important' }}>
                                                {t('users.table.name')}
                                            </Typography>
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell sx={{ backgroundColor: '#FFFFFF' }}>
                                        <TableSortLabel
                                            active={orderBy === 'noUsers'}
                                            direction={orderBy === 'noUsers' ? order : 'asc'}
                                            onClick={(event) => handleRequestSort(event, 'noUsers')}
                                        >
                                            <Typography sx={{ fontSize: '0.875rem', fontWeight: '700 !important' }}>
                                                {t('users.table.noUsers')}
                                            </Typography>
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell sx={{ backgroundColor: '#FFFFFF' }}>
                                        <TableSortLabel
                                            active={orderBy === 'created'}
                                            direction={orderBy === 'created' ? order : 'asc'}
                                            onClick={(event) => handleRequestSort(event, 'created')}
                                        >
                                            <Typography sx={{ fontSize: '0.875rem', fontWeight: '700 !important' }}>
                                                {t('users.table.created')}
                                            </Typography>
                                        </TableSortLabel>
                                    </TableCell>
                                    <TableCell sx={{ width: '5%', backgroundColor: '#FFFFFF' }} />
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filteredGroups?.slice(startIndex, endIndex).map((g, index) => (
                                    <TableRow key={g._id} sx={{
                                        '&:nth-of-type(odd)': {
                                            backgroundColor: 'background.default',
                                        }
                                    }}>
                                        <TableCell>
                                            <Typography sx={{ fontSize: '0.875rem' }}>
                                                {`${g.name}`}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                <Tooltip title={t('users.tooltips.viewMembers')} arrow>
                                                    <Button
                                                        variant=''
                                                        startIcon={<Iconify icon='prime:users' width={20} sx={{ color: 'primary.main' }} />}
                                                        onClick={() => {
                                                            setGroupId(g._id);
                                                            setGroupMembersModal(true);
                                                        }}
                                                        sx={{
                                                            px: 0,
                                                            minWidth: '56px',
                                                            borderRadius: '8px !important',
                                                            backgroundColor: (theme) => ({
                                                                xs: alpha(theme.palette.primary.main, 0.08),
                                                                sm: 'transparent'
                                                            }),
                                                            color: {
                                                                xs: 'primary.main',
                                                                sm: 'text.primary'
                                                            },
                                                            '&:hover': {
                                                                backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.08),
                                                                color: 'primary.main'
                                                            }
                                                        }}
                                                    >
                                                        <Typography sx={{ fontSize: '0.875rem' }}>
                                                            {g.members.length}
                                                        </Typography>
                                                    </Button>
                                                </Tooltip>
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            <Typography sx={{ fontSize: '0.875rem' }}>
                                                {g.created ? fDate(g.created, i18n.language) : null}
                                            </Typography>
                                        </TableCell>
                                        <TableCell align='right'>
                                            <IconButton
                                                disableRipple
                                                disableFocusRipple
                                                onClick={(event) => {
                                                    event.preventDefault();
                                                    setGroupId(g._id);
                                                    setMenuAnchor(event.currentTarget);
                                                }}
                                                sx={{ marginRight: 'auto', zIndex: 9, pr: 0, py: 0 }}
                                            >
                                                <Tooltip title={t('users.table.settings')} arrow>
                                                    <MoreVertIcon fontSize='small' />
                                                </Tooltip>
                                            </IconButton>
                                            <Menu
                                                key={g._id}
                                                anchorEl={menuAnchor}
                                                open={menu && groupId === g._id}
                                                onClose={(event) => {
                                                    event.preventDefault();
                                                    event.stopPropagation();
                                                    setMenuAnchor(null);
                                                    setGroupId(null);
                                                }}
                                            >
                                                <MenuItem
                                                    sx={{ color: 'error.main' }}
                                                    onClick={(event) => {
                                                        event.preventDefault();
                                                        setGroupId(g._id);
                                                        setDeleteGroupModal(true);
                                                        setMenuAnchor(null);
                                                    }}
                                                >
                                                    <ListItemIcon sx={{ color: 'error.main', minWidth: '28px !important' }}>
                                                        <Iconify icon='eva:trash-outline' />
                                                    </ListItemIcon>
                                                    <ListItemText>
                                                        {t('users.table.deleteGroup')}
                                                    </ListItemText>
                                                </MenuItem>
                                            </Menu>
                                        </TableCell>
                                    </TableRow>
                                ))}
                                {groups && search && groups?.length > 0 && filteredGroups?.length === 0 &&
                                    <TableRow>
                                        <TableCell colSpan={6}>
                                            <Grid container>
                                                <Grid item xs={12} sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'center', alignItems: 'center' }}>
                                                    <Iconify icon='pajamas:search-results' width={36} sx={{ mr: 2 }} />
                                                    <Grid item xs={6} md={3}>
                                                        <Typography variant='h6' sx={{ fontSize: '0.875rem !important' }}>
                                                            {t('users.table.empty.title')}
                                                        </Typography>
                                                        <Typography variant='caption' sx={{ fontSize: '0.8rem !important' }}>
                                                            {t('users.table.empty.description')}
                                                        </Typography>
                                                        <Typography variant='caption' sx={{ fontSize: '0.8rem !important', display: 'block' }}>
                                                            {t('users.table.empty.tip')}
                                                        </Typography>
                                                    </Grid>
                                                </Grid>
                                            </Grid>
                                        </TableCell>
                                    </TableRow>
                                }
                                {(!groups || groups?.length === 0) &&
                                    <TableRow>
                                        <TableCell colSpan={6}>
                                            <Grid container>
                                                <Grid item xs={12} sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'center', alignItems: 'center' }}>
                                                    <Iconify icon='tdesign:usergroup-add-filled' width={36} sx={{ mr: 2 }} />
                                                    <Grid item xs={12} md={4}>
                                                        <Typography variant='h6' sx={{ fontSize: '0.875rem !important' }}>
                                                            {t('users.table.noGroups.title')}
                                                        </Typography>
                                                        <Typography variant='caption' sx={{ fontSize: '0.8rem !important' }}>
                                                            {t('users.table.noGroups.description')}
                                                        </Typography>
                                                        <Typography variant='caption' sx={{ fontSize: '0.8rem !important', display: 'block' }}>
                                                            {t('users.table.noGroups.tip')}
                                                        </Typography>
                                                    </Grid>
                                                </Grid>
                                            </Grid>
                                        </TableCell>
                                    </TableRow>
                                }
                            </TableBody>
                        </Table>
                    </TableContainer>
                    {filteredGroups?.length > 0 && (
                        <TablePagination
                            component='div'
                            rowsPerPageOptions={[5, 10, 25, 50, { value: -1, label: t('users.table.all') }]}
                            count={filteredGroups?.length}
                            rowsPerPage={rowsPerPage}
                            page={page}
                            onPageChange={handleChangePage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                            labelRowsPerPage={t('users.table.rows')}
                            labelDisplayedRows={({ from, to, count }) => { return `${from}–${to} ${t('util.pagination.of')} ${count !== -1 ? count : `${t('users.table.moreThan')} ${to}`}`; }}
                        />
                    )}
                </Grid>
            </Grid>
            <NewGroup
                users={users}
                createGroup={createGroup}
                open={newGroupModal}
                setOpen={setNewGroupModal}
            />
            <GroupMembers
                _id={groupId}
                users={users}
                members={groups?.find(g => g._id === groupId)?.members}
                fetchGroups={fetchGroups}
                open={groupMembersModal}
                close={() => {
                    setGroupId(null);
                    setGroupMembersModal(false);
                }}
                setOpen={setOpen}
                setMessage={setMessage}
                setSeverity={setSeverity}
            />
            <ConfirmationModal
                open={deleteGroupModal}
                onClose={() => setDeleteGroupModal(false)}
                title={t('users.modals.removeGroup.title')}
                description={t('users.modals.removeGroup.description')}
                type='remove'
                confirmationText={t('users.modals.removeGroup.confirmation')}
                confirm={deleteGroup}
            />
        </>
    );
}

export default UserGroup;